import { Box, Text, ThemeIcon, Timeline } from "@mantine/core"
import {
  ChatCenteredTextIcon,
  CheckCircleIcon,
  type Icon,
  PackageIcon,
  TruckIcon,
  WarningIcon,
} from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import useSWR from "swr"
import { OrderApi } from "../../apis"
import type { IDeliveryTimelineEntry } from "../../apis/order"
import { dateTimeSecFormat } from "../../utils/date"
import classes from "./DeliveryTimeline.module.css"

type Meta = { Icon: Icon; color: string; label: string }

function metaFor(actionType: string, t: TFunction): Meta {
  switch (actionType) {
    case "DELIVERY_PROGRESS_NOTE":
      return { Icon: ChatCenteredTextIcon, color: "dark", label: t("order.timeline.note", { defaultValue: "Хүргэлтийн мэдээлэл" }) }
    case "PICKED_UP":
      return { Icon: PackageIcon, color: "dark", label: t("order.timeline.pickedUp", { defaultValue: "Бараа хүлээн авав" }) }
    case "OUT_FOR_DELIVERY":
      return { Icon: TruckIcon, color: "dark", label: t("order.timeline.outForDelivery", { defaultValue: "Хүргэлтэд гарлаа" }) }
    case "IN_TRANSIT":
      return { Icon: TruckIcon, color: "dark", label: t("order.timeline.inTransit", { defaultValue: "Замд явж байна" }) }
    case "DELIVERED":
      return { Icon: CheckCircleIcon, color: "green", label: t("order.timeline.delivered", { defaultValue: "Хүргэгдлээ" }) }
    case "FAILED":
      return { Icon: WarningIcon, color: "red", label: t("order.timeline.failed", { defaultValue: "Хүргэлт амжилтгүй" }) }
    case "RETURNED":
      return { Icon: WarningIcon, color: "orange", label: t("order.timeline.returned", { defaultValue: "Буцаагдлаа" }) }
    default:
      return { Icon: ChatCenteredTextIcon, color: "gray", label: actionType }
  }
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return dateTimeSecFormat(d)
}

export function DeliveryTimeline({ orderId }: { orderId: string }) {
  const { t } = useTranslation()
  const { data } = useSWR(
    orderId ? `/ord/order/${orderId}/delivery-timeline` : null,
    () => OrderApi.deliveryTimeline(orderId),
    { revalidateOnFocus: false },
  )

  const rows: IDeliveryTimelineEntry[] = data?.rows ?? []
  if (rows.length === 0) return null

  return (
    // Same white card as the other order-detail cards (canvas OrdB_OrderDetail): bold title, no icon tile.
    <Box className={classes.card} mt="md">
      <Box mb="md">
        <Text className={classes.title}>{t("order.timeline.title", { defaultValue: "Хүргэлтийн явц" })}</Text>
        <Text className={classes.sub}>
          {t("order.timeline.subtitle", { defaultValue: "Нийлүүлэгчээс ирсэн хүргэлтийн мэдээлэл" })}
        </Text>
      </Box>

      <Timeline active={rows.length} bulletSize={26} lineWidth={2}>
        {rows.map((entry) => {
          const meta = metaFor(entry.actionType, t)
          const IconCmp = meta.Icon
          return (
            <Timeline.Item
              key={entry.id}
              bullet={
                <ThemeIcon size={26} radius="xl" variant="filled" color={meta.color}>
                  <IconCmp size={14} weight="bold" />
                </ThemeIcon>
              }
              title={
                <Text fw={600} size="sm">
                  {meta.label}
                </Text>
              }
            >
              {entry.notes && (
                <Text size="sm" c="dark" mt={2} style={{ whiteSpace: "pre-wrap" }}>
                  {entry.notes}
                </Text>
              )}
              <Text className={classes.when} mt={4}>
                {formatWhen(entry.occurredAt)}
              </Text>
            </Timeline.Item>
          )
        })}
      </Timeline>
    </Box>
  )
}
